import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import type { FracSimulation, FractureSurfaceFrame, FractureSurfaceNode } from './types';

const PALETTE = ['#C2533A', '#2874A6', '#1E8449', '#6A4C93', '#D4770C'];
type ScalarMode = 'SCHEME' | 'WIDTH' | 'PROPPANT' | 'CONDUCTIVITY';

const scalarValue = (node: FractureSurfaceNode, mode: ScalarMode): number | null => {
  const value = mode === 'WIDTH' ? Number(node.width) * 1000
    : mode === 'PROPPANT' ? Number(node.proppantArealConcentration)
      : mode === 'CONDUCTIVITY' ? Number(node.conductivity) : NaN;
  return Number.isFinite(value) ? value : null;
};

const surfaceFrames = (simulation: FracSimulation, stageNumber?: number): Array<{
  frame: FractureSurfaceFrame; stageMd: number; schemeIndex: number;
}> => simulation.results.filter((stage) => stageNumber === undefined || stage.stage_number === stageNumber)
  .flatMap((stage) => (stage.clusters ?? []).flatMap((cluster) => {
    const frame = cluster.fracture_surface_frame ?? cluster.surface_frames?.[cluster.surface_frames.length - 1];
    return frame ? [{ frame, stageMd: stage.perf_md, schemeIndex: 0 }] : [];
  }));

export default function Comparison3DView({ simulations, stageNumber }: {
  simulations: FracSimulation[];
  stageNumber?: number;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [mode, setMode] = useState<ScalarMode>('SCHEME');
  const data = useMemo(() => simulations.flatMap((simulation, schemeIndex) => (
    surfaceFrames(simulation, stageNumber).map((item) => ({ ...item, schemeIndex, simulationId: simulation.id }))
  )), [simulations, stageNumber]);
  const values = useMemo(() => data.flatMap(({ frame }) => {
    const sources = frame.model === 'DIRECTIONAL_ADAPTIVE_SURFACE_V1' && frame.wings
      ? Object.values(frame.wings).flatMap((wing) => wing.nodes) : frame.nodes;
    return sources.map((node) => scalarValue(node, mode)).filter((value): value is number => value !== null);
  }), [data, mode]);
  const minimum = Math.min(...values, 0);
  const maximum = Math.max(...values, 1);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !data.length) return undefined;
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xffffff);
    scene.add(new THREE.AmbientLight(0xffffff, 0.85));
    const light = new THREE.DirectionalLight(0xffffff, 0.7);
    light.position.set(1, 2, 3);
    scene.add(light);
    const meshes: THREE.Mesh[] = [];
    const scalarColor = (value: number | null) => {
      if (value === null) return new THREE.Color('#9AA6B2');
      const ratio = Math.max(0, Math.min(1, (value - minimum) / Math.max(1e-12, maximum - minimum)));
      return new THREE.Color().setHSL((1 - ratio) * 0.64, 0.88, 0.48);
    };
    data.forEach(({ frame, stageMd, schemeIndex, simulationId }) => {
      const surfaces = frame.model === 'DIRECTIONAL_ADAPTIVE_SURFACE_V1' && frame.wings
        ? Object.values(frame.wings) : [{ nodes: frame.nodes, faces: frame.faces }];
      surfaces.forEach((surface) => {
        if (!surface.nodes?.length || !surface.faces?.length) return;
        const positions: number[] = [];
        const colors: number[] = [];
        surface.nodes.forEach((node) => {
          positions.push(Number(node.x) || 0, Number(node.z) || 0, stageMd + (Number(node.y) || 0));
          const color = mode === 'SCHEME' ? new THREE.Color(PALETTE[schemeIndex % PALETTE.length]) : scalarColor(scalarValue(node, mode));
          colors.push(color.r, color.g, color.b);
        });
        const indices: number[] = [];
        surface.faces.forEach((face) => {
          for (let index = 1; index < face.length - 1; index += 1) indices.push(face[0], face[index], face[index + 1]);
        });
        const geometry = new THREE.BufferGeometry();
        geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
        geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
        geometry.setIndex(indices);
        geometry.computeVertexNormals();
        const mesh = new THREE.Mesh(geometry, new THREE.MeshPhongMaterial({
          vertexColors: true, side: THREE.DoubleSide, transparent: true,
          opacity: mode === 'SCHEME' ? 0.48 : 0.68, depthWrite: false,
        }));
        mesh.userData = { simulationId };
        scene.add(mesh);
        meshes.push(mesh);
      });
    });
    const bounds = new THREE.Box3();
    meshes.forEach((mesh) => bounds.expandByObject(mesh));
    const center = new THREE.Vector3();
    const size = new THREE.Vector3();
    bounds.getCenter(center); bounds.getSize(size);
    const radius = Math.max(10, size.length() * 0.55);
    const width = container.clientWidth; const height = container.clientHeight;
    const camera = new THREE.PerspectiveCamera(42, width / Math.max(1, height), 0.1, radius * 30);
    camera.position.copy(center).add(new THREE.Vector3(radius * 1.2, radius * 0.9, radius * 1.5));
    camera.lookAt(center);
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(width, height);
    container.appendChild(renderer.domElement);
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.target.copy(center); controls.enableDamping = true; controls.update();
    let frameId = 0;
    const render = () => { frameId = requestAnimationFrame(render); controls.update(); renderer.render(scene, camera); };
    render();
    const resize = () => {
      const nextWidth = container.clientWidth; const nextHeight = container.clientHeight;
      camera.aspect = nextWidth / Math.max(1, nextHeight); camera.updateProjectionMatrix();
      renderer.setSize(nextWidth, nextHeight);
    };
    const observer = new ResizeObserver(resize); observer.observe(container);
    return () => {
      cancelAnimationFrame(frameId); observer.disconnect(); controls.dispose(); renderer.dispose();
      meshes.forEach((mesh) => { mesh.geometry.dispose(); (mesh.material as THREE.Material).dispose(); });
      renderer.domElement.remove();
    };
  }, [data, mode, minimum, maximum]);

  return <div style={{ height: 300, position: 'relative', border: '1px solid #D6DAE0', background: '#fff' }}>
    <div style={{ position: 'absolute', zIndex: 2, left: 8, top: 7, display: 'flex', gap: 6, alignItems: 'center', fontSize: 10 }}>
      <strong>真实裂缝面统一坐标对比</strong>
      <select value={mode} onChange={(event) => setMode(event.target.value as ScalarMode)} style={{ fontSize: 10 }}>
        <option value="SCHEME">按方案着色</option><option value="WIDTH">统一缝宽色标</option>
        <option value="PROPPANT">统一支撑剂铺置色标</option><option value="CONDUCTIVITY">统一导流能力色标</option>
      </select>
      {mode !== 'SCHEME' && <span>{values.length ? `${minimum.toFixed(3)} – ${maximum.toFixed(3)}` : '该字段无有效数据'}</span>}
    </div>
    {!data.length && <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#708399', fontSize: 11 }}>所选模拟未保存真实裂缝面，不能生成三维对比。</div>}
    <div ref={containerRef} style={{ width: '100%', height: '100%' }} />
  </div>;
}
